import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";

import { api } from "../lib/api";
import { cn } from "../lib/cn";

interface ScheduleEntry {
  sourceId: string;
  name: string;
  connectorId: string;
  cron: string;
  enabled: boolean;
  lastRunAt?: string;
  lastStatus?: string;
  nextRunAt?: string;
}

type Mode = "calendar" | "table";

export function SchedulePage() {
  const [mode, setMode] = useState<Mode>("calendar");
  const schedule = useQuery({
    queryKey: ["schedule"],
    queryFn: () => api.get<ScheduleEntry[]>("/api/v1/schedule"),
    refetchInterval: 30_000,
  });
  const data = schedule.data ?? [];

  return (
    <div className="mx-auto w-full max-w-[1200px] px-10 pb-16">
      <header className="flex items-end justify-between gap-6 pt-12">
        <div>
          <div className="eyebrow">Cadence</div>
          <h1 className="mt-3 font-display text-[40px] font-semibold leading-tight tracking-tightest text-ink">
            Schedule
          </h1>
          <p className="mt-2 max-w-xl text-[14px] leading-relaxed text-ash">
            What the agent intends to crawl over the next 7 days. Click any
            row or chip to open that source's detail page.
          </p>
        </div>
        <div className="flex rounded-md border border-rule bg-surface p-0.5">
          <ModeButton active={mode === "calendar"} onClick={() => setMode("calendar")}>
            Calendar
          </ModeButton>
          <ModeButton active={mode === "table"} onClick={() => setMode("table")}>
            Table
          </ModeButton>
        </div>
      </header>

      {schedule.isLoading ? (
        <p className="mt-8 text-sm text-ash">Loading…</p>
      ) : null}

      {data.length === 0 ? (
        <p className="mt-8 text-sm text-ash">
          No sources configured. Add one to start scheduling crawls.
        </p>
      ) : mode === "calendar" ? (
        <CalendarView entries={data} />
      ) : (
        <TableView entries={data} />
      )}
    </div>
  );
}

function ModeButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "rounded-[5px] px-3 py-1 text-[12.5px] font-medium tracking-tight transition-colors",
        active ? "bg-ink text-white" : "text-ash hover:text-ink",
      )}
    >
      {children}
    </button>
  );
}

// CalendarView lays out a 7-day grid of next firings. Each entry's
// nextRunAt is placed in the day column it belongs to. Recurring sources
// only show one chip per day (the next firing in that day) so the canvas
// stays scannable on busy plants.
function CalendarView({ entries }: { entries: ScheduleEntry[] }) {
  const [, setLocation] = useLocation();

  const days = useMemo(() => {
    const out: { date: Date; label: string; iso: string }[] = [];
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    for (let i = 0; i < 7; i++) {
      const d = new Date(now);
      d.setDate(d.getDate() + i);
      out.push({
        date: d,
        label:
          i === 0
            ? "Today"
            : i === 1
              ? "Tomorrow"
              : d.toLocaleDateString(undefined, { weekday: "long" }),
        iso: d.toISOString().slice(0, 10),
      });
    }
    return out;
  }, []);

  const byDay = useMemo(() => {
    const m = new Map<string, ScheduleEntry[]>();
    for (const day of days) m.set(day.iso, []);
    for (const e of entries) {
      if (!e.nextRunAt) continue;
      const iso = new Date(e.nextRunAt).toISOString().slice(0, 10);
      if (!m.has(iso)) continue;
      m.get(iso)!.push(e);
    }
    for (const arr of m.values()) {
      arr.sort((a, b) =>
        (a.nextRunAt ?? "").localeCompare(b.nextRunAt ?? ""),
      );
    }
    return m;
  }, [entries, days]);

  // Anything without a next run lives in a "No schedule" rail at the bottom.
  const unscheduled = useMemo(
    () => entries.filter((e) => !e.nextRunAt),
    [entries],
  );

  return (
    <section className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-7">
      {days.map((day) => {
        const list = byDay.get(day.iso) ?? [];
        return (
          <div key={day.iso} className="card overflow-hidden">
            <div className="border-b border-rule bg-paper px-4 py-3">
              <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-ash">
                {day.label}
              </div>
              <div className="mt-0.5 font-display text-[15px] font-semibold tracking-tight text-ink">
                {day.date.toLocaleDateString(undefined, {
                  month: "short",
                  day: "numeric",
                })}
              </div>
            </div>
            <ul className="min-h-[140px] space-y-2 p-3">
              {list.length === 0 ? (
                <li className="px-1 py-2 text-[11px] text-ash">No runs.</li>
              ) : null}
              {list.map((e) => (
                <li key={e.sourceId}>
                  <button
                    onClick={() =>
                      setLocation(`/sources/${encodeURIComponent(e.sourceId)}`)
                    }
                    className="block w-full rounded-md border border-rule bg-surface px-2.5 py-2 text-left transition-colors hover:border-brand-500/40 hover:bg-brand-50"
                  >
                    <div className="font-mono text-[10.5px] text-ash">
                      {e.nextRunAt
                        ? new Date(e.nextRunAt).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : ""}
                    </div>
                    <div className="mt-0.5 truncate text-[12.5px] font-semibold text-ink">
                      {e.name}
                    </div>
                    <div className="font-mono text-[10px] text-ash">
                      {e.connectorId}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        );
      })}

      {unscheduled.length > 0 ? (
        <div className="sm:col-span-7 mt-6">
          <div className="eyebrow">No schedule</div>
          <p className="mt-1 text-[12.5px] text-ash">
            On-demand only — not on the calendar. {unscheduled.length}{" "}
            {unscheduled.length === 1 ? "source" : "sources"}.
          </p>
          <ul className="mt-3 flex flex-wrap gap-2">
            {unscheduled.map((e) => (
              <li key={e.sourceId}>
                <button
                  onClick={() =>
                    setLocation(`/sources/${encodeURIComponent(e.sourceId)}`)
                  }
                  className="rounded-md border border-rule bg-surface px-2.5 py-1.5 text-[12px] text-ink hover:border-ink/30 hover:bg-paper"
                >
                  {e.name}{" "}
                  <span className="font-mono text-[10.5px] text-ash">
                    {e.connectorId}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}

// TableView shows the same data as the calendar but with bulk-edit
// affordances for changing schedule en-masse (pause / resume) and a
// sortable "next run" column.
function TableView({ entries }: { entries: ScheduleEntry[] }) {
  const [, setLocation] = useLocation();
  const qc = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const setEnabled = useMutation({
    mutationFn: async (args: { ids: string[]; enabled: boolean }) => {
      await Promise.all(
        args.ids.map((id) =>
          api.patch(`/api/v1/sources/${encodeURIComponent(id)}`, {
            enabled: args.enabled,
          }),
        ),
      );
    },
    onSuccess: () => {
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ["schedule"] });
      qc.invalidateQueries({ queryKey: ["sources"] });
    },
  });

  const refreshNow = useMutation({
    mutationFn: async (ids: string[]) => {
      await Promise.all(
        ids.map((id) => api.post(`/api/v1/sources/${encodeURIComponent(id)}/refresh`)),
      );
    },
    onSuccess: () => {
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ["schedule"] });
    },
  });

  return (
    <section className="mt-8 card overflow-hidden">
      {selected.size > 0 ? (
        <div className="flex items-center justify-between border-b border-rule bg-brand-50/60 px-5 py-3">
          <span className="text-[12.5px] text-ink">
            {selected.size} selected
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setEnabled.mutate({ ids: [...selected], enabled: false })}
              className="btn-secondary text-[12px]"
            >
              Pause selected
            </button>
            <button
              onClick={() => setEnabled.mutate({ ids: [...selected], enabled: true })}
              className="btn-secondary text-[12px]"
            >
              Resume selected
            </button>
            <button
              onClick={() => refreshNow.mutate([...selected])}
              className="btn-primary text-[12px]"
            >
              Run now
            </button>
          </div>
        </div>
      ) : null}
      <table className="w-full text-sm">
        <thead className="border-b border-rule bg-paper text-left">
          <tr>
            <Th></Th>
            <Th>Source</Th>
            <Th>Cron</Th>
            <Th>Next run</Th>
            <Th>Last run</Th>
            <Th>Status</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-rule">
          {entries.map((e) => (
            <tr
              key={e.sourceId}
              className="transition-colors hover:bg-paper"
            >
              <Td>
                <input
                  type="checkbox"
                  checked={selected.has(e.sourceId)}
                  onChange={() => toggle(e.sourceId)}
                  className="h-4 w-4 rounded border-rule text-brand-600"
                />
              </Td>
              <Td>
                <button
                  onClick={() =>
                    setLocation(`/sources/${encodeURIComponent(e.sourceId)}`)
                  }
                  className="text-left"
                >
                  <div className="font-semibold text-ink hover:underline">
                    {e.name}
                  </div>
                  <div className="font-mono text-[10.5px] text-ash">
                    {e.connectorId}
                  </div>
                </button>
              </Td>
              <Td>
                {e.cron ? (
                  <span className="font-mono text-[11.5px] text-ink">
                    {e.cron}
                  </span>
                ) : (
                  <span className="text-[11.5px] text-ash">On-demand</span>
                )}
              </Td>
              <Td>
                {e.nextRunAt ? (
                  <span className="text-ink">
                    {new Date(e.nextRunAt).toLocaleString()}
                  </span>
                ) : (
                  <span className="text-ash">—</span>
                )}
              </Td>
              <Td>
                {e.lastRunAt ? (
                  <span className="text-ash">
                    {new Date(e.lastRunAt).toLocaleString()}
                  </span>
                ) : (
                  <span className="text-ash">Never</span>
                )}
              </Td>
              <Td>
                {!e.enabled ? (
                  <span className="chip-neutral">Paused</span>
                ) : (
                  <StatusBadge status={e.lastStatus} />
                )}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function StatusBadge({ status }: { status?: string }) {
  if (!status) return <span className="chip-neutral">Never run</span>;
  if (status === "succeeded") return <span className="chip-emerald">Succeeded</span>;
  if (status === "failed") return <span className="chip-rose">Failed</span>;
  if (status === "running") return <span className="chip-amber">Running</span>;
  return <span className="chip-neutral">{status}</span>;
}

function Th({ children }: { children?: React.ReactNode }) {
  return (
    <th className="px-5 py-3 font-mono text-[10.5px] font-semibold uppercase tracking-[0.18em] text-ash">
      {children}
    </th>
  );
}

function Td({ children }: { children?: React.ReactNode }) {
  return <td className="px-5 py-4 align-top">{children}</td>;
}
